import React, { useEffect, useState } from 'react';
import { Button, Input, Space, Table } from 'antd';
import { SearchOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import TableSkeleton from './components/TableSkeleton';
import { Badge } from './components/StatusPill';
import axiosInstance from './utils/axiosInstance';
import { invalidateNavCounts } from './hooks/useNavCounts';
import { money } from './utils/format';
import { confirmDialog, errorDialog } from './utils/dialog';
import { useNavigate } from 'react-router-dom';

const { Column } = Table;

interface WithdrawalType {
    key: React.Key;
    id: number;
    withdrawalStatus: string;
    createdAt: Date;
    totalAmount: number;
    User: {
        username: string;
        phoneNo: string;

        bank: string;
        account: string;
        name: string;
    },
    totalDuplicate: number;
    duplicateUsers: string;
    duplicateDetails?: string;
}


const WithdrawalPage: React.FC = () => {
    const [withdrawals, setWithdrawals] = useState<WithdrawalType[]>([]);
    const [loading, setLoading] = useState(true);
    const [username, setUsername] = useState<string>();
    const [phoneNo, setPhoneNo] = useState<string>();

    const navigate = useNavigate();

    const getWithdrawals = async () => {
        setLoading(true);
        try {
        const response = await axiosInstance.post("admin/protected/getWithdrawals", {
            username,
            phoneNo
        })
        const data = response?.data?.data;
        if (data) {
            console.log(data)
            setWithdrawals(data.map((e: any) => {
                const user = e.User;

                return {
                    id: e.id,
                    key: e.id,
                    username: e.username,
                    phoneNo: e.phoneNo,
                    clickRemaining: e.clickRemaining,
                    totalCommission: e.totalCommission,
                    walletBalances: e.walletBalances,
                    referralId: e.referralId,
                    createdAt: new Date(e.createdAt),
                    withdrawalStatus: e.withdrawalStatus,
                    totalAmount: e.totalAmount,

                    duplicateUsers: e.duplicateUsers,
                    totalDuplicate: e.totalDuplicate,
                    duplicateDetails: e.duplicateDetails,

                    User: {
                        account: user.UserBank?.account ?? "",
                        bank: user.UserBank?.bank ?? "",
                        name: user.UserBank?.name ?? "",

                        phoneNo: user.phoneNo,
                        username: user.username,
                    }
                } as WithdrawalType;
            }));
        }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getWithdrawals();
    }, [])


    const completeWithdrawal = async (withdraw: WithdrawalType) => {
        if (!await confirmDialog({ title: '确定完成此提款？' })) return;
        try {
            await axiosInstance.post("admin/protected/completeWithdrawal", {
                id: withdraw.id,
            })
            getWithdrawals();
            invalidateNavCounts();
        } catch (error: any) {
            errorDialog();
        }
    }

    const rejectWithdrawal = async (withdraw: WithdrawalType) => {
        if (!await confirmDialog({ title: '确定拒绝此提款？' })) return;
        try {
            await axiosInstance.post("admin/protected/rejectWithdrawal", {
                id: withdraw.id,
            })
            getWithdrawals();
            invalidateNavCounts();
        } catch (error: any) {
            errorDialog();
        }
    }

    return (
        <AppShell title="提款" subtitle={`${withdrawals.length} 提款申请`}>
            <Panel className="mb-4">
                <div className='flex flex-wrap gap-3'>
                    <Input className='max-w-[220px]' allowClear placeholder="用户名" onChange={(e) => setUsername(e.target.value)} onPressEnter={getWithdrawals} />
                    <Input className='max-w-[220px]' allowClear placeholder="手机号" onChange={(e) => setPhoneNo(e.target.value)} onPressEnter={getWithdrawals} />
                    <Button type="primary" icon={<SearchOutlined />} onClick={getWithdrawals}>搜索</Button>
                </div>
            </Panel>

            <Panel flush>
            {loading ? <TableSkeleton rows={5} columns={7} /> : (
            <Table<WithdrawalType> dataSource={withdrawals} scroll={{ x: 'max-content' }} size="middle">
                <Column title="ID" dataIndex="id" key="id" width={70} />
                <Column
                    title="用户名"
                    key="username"
                    render={(_: any, record: WithdrawalType) => (
                        <span className="font-semibold text-ink">{record.User.username}</span>
                    )}
                />
                <Column
                    title="手机号"
                    key="phoneNo"
                    render={(_: any, record: WithdrawalType) => (
                        <span className="tabular text-muted">{record.User.phoneNo}</span>
                    )}
                />
                <Column title="提款数" key="totalAmount" className='tabular'
                    render={(_: any, record: WithdrawalType) => (
                        <span className="font-semibold text-ink">
                            <span className="mr-1 text-xs font-normal text-faint">RM</span>
                            {money(record.totalAmount)}
                        </span>
                    )}
                />
                <Column
                    title="银行资料"
                    key="bank"
                    render={(_: any, record: WithdrawalType) => (
                        <div className="min-w-[180px] text-xs leading-relaxed">
                            <div><span className="text-faint">银行: </span>{record.User.bank}</div>
                            <div><span className="text-faint">户口: </span><span className='tabular'>{record.User.account}</span></div>
                            <div><span className="text-faint">接收人: </span>{record.User.name}</div>
                        </div>
                    )}
                />
                <Column
                    title="重叠用户/银行防刷"
                    key="duplicate"
                    render={(_: any, record: WithdrawalType) => (
                        record.totalDuplicate <= 0
                            ? <span className="text-faint">无</span>
                            : (
                                <div className="text-xs leading-relaxed">
                                    <Badge tone="caution">重叠 {record.totalDuplicate}</Badge>
                                    <div className="text-muted mt-1">{record.duplicateDetails || `用户ID: ${record.duplicateUsers}`}</div>
                                </div>
                            )
                    )}
                />
                <Column
                    title="状态"
                    key="status"
                    render={(_: any, record: WithdrawalType) => (
                        record.withdrawalStatus === "completed" ? <Badge tone="positive" dot>已完成</Badge> :
                            record.withdrawalStatus === "rejected" ? <Badge tone="negative" dot>已拒绝</Badge> :
                                <Badge tone="caution" dot>待处理</Badge>
                    )}
                />
                <Column
                    title="操作"
                    key="action"
                    fixed="right"
                    width={170}
                    render={(_: any, record: WithdrawalType) => (
                        <Space size="small">
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckOutlined />}
                                disabled={record.withdrawalStatus !== "pending"}
                                onClick={() => completeWithdrawal(record)}>完成</Button>
                            <Button
                                size="small"
                                danger
                                icon={<CloseOutlined />}
                                disabled={record.withdrawalStatus !== "pending"}
                                onClick={() => rejectWithdrawal(record)}>拒绝</Button>
                        </Space>
                    )}
                />
            </Table>
            )}
            </Panel>
        </AppShell>
    );
}

export default WithdrawalPage;